/**
 * User Types
 * Epic 9, Story 9.2: User Management Interface
 * Types for user listing, role management, and user removal
 */

/**
 * User roles in the system
 * - owner: Full access, can manage team
 * - member: Can manage campaigns and content (the non-owner working role)
 * - viewer: Read-only access
 *
 * (Superuser is an orthogonal `is_superuser` flag, not a role.)
 */
export type UserRole = 'owner' | 'member' | 'viewer';

/**
 * User status
 * - active: User account is active
 * - pending: Invited but not yet registered (from invitations)
 */
export type UserStatus = 'active' | 'pending';

/**
 * Role display names for UI
 */
export const USER_ROLE_DISPLAY_NAMES: Record<UserRole, string> = {
  owner: 'Owner',
  member: 'Member',
  viewer: 'Viewer',
};

/**
 * Roles that can be assigned to users (excludes owner)
 */
export type AssignableRole = Exclude<UserRole, 'owner'>;

/**
 * Assignable role options for role change dropdown
 */
export const ASSIGNABLE_ROLES: { value: AssignableRole; label: string }[] = [
  { value: 'member', label: 'Member' },
  { value: 'viewer', label: 'Viewer' },
];

/**
 * User record from the API
 * Represents an active user in the organization
 */
export interface User {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  status: UserStatus;
  joined_at: string;
  last_active_at: string | null;
  is_owner: boolean;
}

/**
 * Response from GET /api/users
 * Lists all users in the tenant
 */
export interface ListUsersResponse {
  success: true;
  data: {
    users: User[];
  };
}

/**
 * Request to update a user's role
 * PUT /api/user/role?id=xxx
 */
export interface UpdateRoleRequest {
  role: AssignableRole;
}

/**
 * Response from role update
 */
export interface UpdateRoleResponse {
  success: true;
  data: {
    id: string;
    role: AssignableRole;
    updated_at: string;
  };
}

/**
 * Response from user removal
 * DELETE /api/user?id=xxx
 */
export interface RemoveUserResponse {
  success: true;
  data: {
    removed_user_id: string;
    reassigned_count: number;
  };
}

/**
 * Error codes for user management operations
 */
export type UserManagementErrorCode =
  | 'CANNOT_MODIFY_OWNER'  // Cannot change owner's role
  | 'CANNOT_REMOVE_SELF'   // Cannot remove yourself
  | 'CANNOT_REMOVE_OWNER'  // Cannot remove the owner
  | 'NOT_FOUND'            // User not found
  | 'FORBIDDEN'            // Not authorized (non-owner)
  | 'INVALID_INPUT';       // Invalid input data

/**
 * Error response for user management operations
 */
export interface UserManagementErrorResponse {
  success: false;
  error: {
    code: UserManagementErrorCode;
    message: string;
  };
}
